/**
 * MovementPrediction - client-side input history and prediction for networking.
 * Stores inputs with sequence numbers in a circular buffer for reconciliation.
 */

import type * as THREE from 'three';

type NetTransform = {
  position: THREE.Vector3;
  rotation: THREE.Quaternion;
  scale: THREE.Vector3;
};

type TimedTransform = NetTransform & {
  timestamp: number;
};

export class NetMovementPredictor {
  public static PredictionEnabled: boolean = true;
  public static LerpAlpha: number = 0.4;
  private remoteBuffer: TimedTransform[] = [];
  private localBuffer: TimedTransform[] = [];
  constructor(private maxCachedFrames: number = 100) {}

  public static currentTime(): number {
    return Date.now();
  }

  /*
  * Returns current * (1 - alpha) + desired * alpha
  */
  public static lerpTransform(current: NetTransform, desired: NetTransform, alpha: number = NetMovementPredictor.LerpAlpha): NetTransform {
    return {
      position: current.position.clone().lerp(desired.position, alpha),
      rotation: current.rotation.clone().slerp(desired.rotation, alpha),
      scale: current.scale.clone().lerp(desired.scale, alpha),
    };
  }

  public addRemoteTransform(transform: TimedTransform): void {
    this.remoteBuffer.push(transform);
    if (this.remoteBuffer.length > this.maxCachedFrames) {
      this.remoteBuffer.shift();
    }
  }

  public addLocalTransform(transform: TimedTransform): void {
    this.localBuffer.push(transform);
    if (this.localBuffer.length > this.maxCachedFrames) {
      this.localBuffer.shift();
    }
  }

  // Interpolates/extrapolates the local buffer to find the transform at a given timestamp
  private getLocalAtTimestamp(targetTimestamp: number): TimedTransform | null {
    const buf = this.localBuffer;
    if (buf.length === 0) return null;
    if (targetTimestamp <= buf[0].timestamp) return buf[0];
    if (targetTimestamp >= buf[buf.length - 1].timestamp) return buf[buf.length - 1];
    for (let i = 0; i < buf.length - 1; ++i) {
      const a = buf[i], b = buf[i + 1];
      if (targetTimestamp >= a.timestamp && targetTimestamp <= b.timestamp) {
        const t = (targetTimestamp - a.timestamp) / (b.timestamp - a.timestamp);
        return {
          timestamp: targetTimestamp,
          position: a.position.clone().lerp(b.position, t),
          rotation: a.rotation.clone().slerp(b.rotation, t),
          scale: a.scale.clone().lerp(b.scale, t),
        };
      }
    }
    return buf[buf.length - 1];
  }

  public predictCurrentTransform(useSimpleInterpolation: boolean = false): {
    position: THREE.Vector3,
    rotation: THREE.Quaternion,
    scale: THREE.Vector3
  } | null {
    if (this.remoteBuffer.length === 0) {
      return null;
    }

    // Simple interpolation mode for simulated proxies (no local simulation)
    // Just interpolate between the two most recent server states
    if (useSimpleInterpolation) {
      if (this.remoteBuffer.length < 2) {
        return this.remoteBuffer[this.remoteBuffer.length - 1];
      }

      const now = NetMovementPredictor.currentTime();
      const latest = this.remoteBuffer[this.remoteBuffer.length - 1];
      const previous = this.remoteBuffer[this.remoteBuffer.length - 2];

      // Interpolate between previous and latest based on time
      const timeSinceLatest = now - latest.timestamp;
      const timeBetweenUpdates = latest.timestamp - previous.timestamp;

      if (timeBetweenUpdates <= 0 || timeSinceLatest < 0) {
        return latest;
      }

      // Disable extrapolation to prevent jumping ahead
      const maxT = 1.0;
      const t = Math.min(maxT, timeSinceLatest / timeBetweenUpdates);

      const result = {
        position: previous.position.clone().lerp(latest.position, t),
        rotation: previous.rotation.clone().slerp(latest.rotation, t),
        scale: previous.scale.clone().lerp(latest.scale, t),
      };

      return result;
    }

    // Prediction mode for autonomous proxies (with local simulation)
    if (this.localBuffer.length === 0) {
      return null;
    }

    if (!NetMovementPredictor.PredictionEnabled) {
      return this.remoteBuffer[this.remoteBuffer.length - 1];
    }

    const latestRemote = this.remoteBuffer[this.remoteBuffer.length - 1];
    const latestLocal = this.localBuffer[this.localBuffer.length - 1];
    const clockDiff = latestLocal.timestamp - latestRemote.timestamp;

    // 1. Target local timestamp that matches latest remote, shifted by clock diff
    const localAnchorTime = latestRemote.timestamp + clockDiff;

    // 2. Interpolate/extrapolate local buffer to get transform at that anchor time
    const localAtAnchor = this.getLocalAtTimestamp(localAnchorTime);
    if (!localAtAnchor) {
      // Should not happen, fallback to latest local
      return {
        position: latestLocal.position.clone(),
        rotation: latestLocal.rotation.clone(),
        scale: latestLocal.scale.clone(),
      };
    }

    // 3. Calculate local movement since anchor
    const deltaPos = latestLocal.position.clone().sub(localAtAnchor.position);
    const deltaRot = latestLocal.rotation.clone().multiply(localAtAnchor.rotation.clone().invert());
    const deltaScale = latestLocal.scale.clone().divide(localAtAnchor.scale);

    // 4. Apply delta to latest remote
    return {
      position: latestRemote.position.clone().add(deltaPos),
      rotation: latestRemote.rotation.clone().multiply(deltaRot),
      scale: latestRemote.scale.clone().multiply(deltaScale),
    };
  }
}

